import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import SpinnerLoader from '../../utils/SpinnerLoader';
import { images } from '../../utils/images';
import { MAP_CONFIG } from '../FMCR/VehicleTracking/trackingUtils';

const CENTER_PAKISTAN = [69.3451, 30.3753]; // [lng, lat]

const decodePolyline = (encoded = '') => {
  let index = 0, lat = 0, lng = 0;
  const path = [];
  while (index < encoded.length) {
    let shift = 0, result = 0, byte = null;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20 && index < encoded.length);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    shift = 0; result = 0;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20 && index < encoded.length);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    path.push([lng / 1e5, lat / 1e5]);
  }
  return path.filter(p => Number.isFinite(p[0]) && Number.isFinite(p[1]));
};

const toCoord = (source = {}, latKey = 'latitude', lngKey = 'longitude') => {
  const lat = Number(source?.[latKey]);
  const lng = Number(source?.[lngKey]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lng, lat];
};

const makeCirclePolygon = (center, radiusMeters, steps = 64) => {
  const [lng, lat] = center;
  const R = 6371000;
  const coords = [];
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    const dLat = (radiusMeters / R) * Math.cos(angle) * (180 / Math.PI);
    const dLng = (radiusMeters / R) * Math.sin(angle) * (180 / Math.PI) / Math.cos(lat * Math.PI / 180);
    coords.push([lng + dLng, lat + dLat]);
  }
  return { type: 'Polygon', coordinates: [coords] };
};

const makeMarkerEl = (src, size = 28, title = '') => {
  const el = document.createElement('img');
  el.src = src;
  el.title = title;
  el.style.cssText = `width:${size}px;height:${size}px;cursor:pointer;display:block;`;
  return el;
};

const FleetOverviewMapLibre = ({
  geofences,
  setGeofences,
  isGeofenceMode,
  selectedGeofence,
  setSelectedGeofence,
  handleRadiusChanged,
  handleCenterChanged,
  customCenter = null,
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tripMarkersRef = useRef([]);
  const customMarkerRef = useRef(null);
  const popupRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);

  const { viewAllTripsData, viewAllTripsLoading } = useSelector(s => s.trips);

  // Initialize map
  useEffect(() => {
    const init = () => {
      const ml = window.maplibregl;
      if (!ml || !mapContainerRef.current) return;

      const prov = MAP_CONFIG.providers.esri;
      const map = new ml.Map({
        container: mapContainerRef.current,
        style: {
          version: 8,
          sources: {
            basemap: { type: 'raster', tiles: prov.tiles, tileSize: prov.tileSize || 256, maxzoom: prov.maxzoom || 17, attribution: prov.attribution || '' },
          },
          layers: [
            { id: 'bg', type: 'background', paint: { 'background-color': '#e9edf2' } },
            { id: 'basemap-layer', type: 'raster', source: 'basemap', paint: { 'raster-opacity': 1, 'raster-fade-duration': 100 } },
          ],
        },
        center: CENTER_PAKISTAN,
        zoom: 5.5,
        maxZoom: 18,
        dragRotate: false,
        pitchWithRotate: false,
        attributionControl: false,
      });

      map.addControl(new ml.NavigationControl({ showCompass: false }), 'top-right');

      map.on('load', () => {
        map.addSource('trip-routes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({ id: 'trip-routes-shadow', type: 'line', source: 'trip-routes', paint: { 'line-color': '#3e3e3e', 'line-width': ['case', ['get', 'selected'], 11, 0], 'line-opacity': 0.9 } });
        map.addLayer({ id: 'trip-routes-line', type: 'line', source: 'trip-routes', paint: { 'line-color': '#156119', 'line-width': ['case', ['get', 'selected'], 7, 5], 'line-opacity': ['case', ['get', 'selected'], 0.95, 0.75] } });

        map.addSource('geofences', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({ id: 'geofences-fill', type: 'fill', source: 'geofences', paint: { 'fill-color': '#F0A842', 'fill-opacity': 0.25 } });
        map.addLayer({ id: 'geofences-outline', type: 'line', source: 'geofences', paint: { 'line-color': '#F0A842', 'line-width': 2, 'line-opacity': 0.8 } });
        map.addLayer({ id: 'geofences-outline-selected', type: 'line', source: 'geofences', filter: ['==', ['get', 'selected'], true], paint: { 'line-color': '#e65c00', 'line-width': 3, 'line-opacity': 1 } });

        mapInstanceRef.current = map;
        setMapLoaded(true);
      });
    };

    if (window.maplibregl) {
      init();
    } else {
      const t = setInterval(() => { if (window.maplibregl) { clearInterval(t); init(); } }, 200);
      return () => clearInterval(t);
    }

    return () => {
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, []);

  // Map click for geofence drawing
  const clickStateRef = useRef({ isGeofenceMode: false, selectedGeofence: null });
  useEffect(() => { clickStateRef.current = { isGeofenceMode, selectedGeofence }; }, [isGeofenceMode, selectedGeofence]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded) return;
    const handler = (e) => {
      const { isGeofenceMode: gm, selectedGeofence: sg } = clickStateRef.current;
      if (!gm) { setSelectedGeofence(null); return; }
      if (sg) { setSelectedGeofence(null); return; }
      const { lng, lat } = e.lngLat;
      const newGeo = {
        id: Date.now(),
        center: { lat, lng },
        radius: 5000,
        address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        options: { strokeColor: '#F0A842', fillColor: '#F0A842', visible: true },
      };
      setGeofences(prev => [...prev, newGeo]);
      setSelectedGeofence(newGeo);
    };
    map.on('click', handler);
    return () => map.off('click', handler);
  }, [mapLoaded, setGeofences, setSelectedGeofence]);

  // Fly to customCenter
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || !customCenter) return;
    const { lat, lng } = customCenter;
    if (Number.isFinite(lat) && Number.isFinite(lng)) map.flyTo({ center: [lng, lat], zoom: 12 });
  }, [customCenter, mapLoaded]);

  // Custom center marker
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded) return;
    const ml = window.maplibregl;
    if (customMarkerRef.current) { customMarkerRef.current.remove(); customMarkerRef.current = null; }
    if (customCenter && Number.isFinite(customCenter.lat) && Number.isFinite(customCenter.lng)) {
      customMarkerRef.current = new ml.Marker({ color: '#4014B8' }).setLngLat([customCenter.lng, customCenter.lat]).addTo(map);
    }
  }, [customCenter, mapLoaded]);

  // Render trip markers and routes
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded) return;
    const ml = window.maplibregl;

    tripMarkersRef.current.forEach(m => m.remove());
    tripMarkersRef.current = [];

    const features = [];
    if (!Array.isArray(viewAllTripsData)) {
      const src = map.getSource('trip-routes');
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    const allCoords = [];
    viewAllTripsData.forEach(trip => {
      const start = toCoord(trip.start_trip);
      const end = toCoord(trip.end_trip);
      const current = toCoord(trip.trip_current_detail, 'lat', 'lng');

      const polylineObj = trip?.polyline;
      const encoded = String((typeof polylineObj === 'string' ? polylineObj : null) || polylineObj?.full_polyline || polylineObj?.polyline || trip?.full_polyline || '').trim();
      let path = encoded ? decodePolyline(encoded) : [];
      if (path.length < 2) path = [start, end].filter(Boolean);

      const tripInfo = {
        id: trip.id, tripId: trip.trip_id || trip.id,
        start, end, current,
        dispatchLocation: trip.start_trip?.location,
        decantingLocation: trip.end_trip?.location,
      };

      if (path.length > 1) {
        path.forEach(c => allCoords.push(c));
        features.push({ type: 'Feature', properties: { id: trip.id, selected: selectedTrip?.id === trip.id }, geometry: { type: 'LineString', coordinates: path } });
      }

      if (current) {
        const el = makeMarkerEl(images.runningRoute, 28, 'Current position');
        el.addEventListener('click', (e) => { e.stopPropagation(); setSelectedTrip(t => t?.id === trip.id && t?.type === 'current' ? null : { ...tripInfo, type: 'current' }); });
        tripMarkersRef.current.push(new ml.Marker({ element: el }).setLngLat(current).addTo(map));
        allCoords.push(current);
      }
      if (start) {
        const el = makeMarkerEl(images.startMap, selectedTrip?.id === trip.id ? 36 : 28, tripInfo.dispatchLocation || 'Start point');
        el.addEventListener('click', (e) => { e.stopPropagation(); setSelectedTrip(t => t?.tripId === tripInfo.tripId && t?.type === 'start' ? null : { ...tripInfo, type: 'start' }); });
        tripMarkersRef.current.push(new ml.Marker({ element: el }).setLngLat(start).addTo(map));
        allCoords.push(start);
      }
      if (end) {
        const el = makeMarkerEl(images.trip_end, selectedTrip?.id === trip.id ? 36 : 28, tripInfo.decantingLocation || 'End point');
        el.addEventListener('click', (e) => { e.stopPropagation(); setSelectedTrip(t => t?.tripId === tripInfo.tripId && t?.type === 'end' ? null : { ...tripInfo, type: 'end' }); });
        tripMarkersRef.current.push(new ml.Marker({ element: el }).setLngLat(end).addTo(map));
        allCoords.push(end);
      }
    });

    const src = map.getSource('trip-routes');
    if (src) src.setData({ type: 'FeatureCollection', features });

    if (!customCenter && allCoords.length > 0) {
      const lngs = allCoords.map(c => c[0]);
      const lats = allCoords.map(c => c[1]);
      map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 60, maxZoom: 14 });
    }
  }, [viewAllTripsData, mapLoaded, selectedTrip, customCenter]);

  // Popup for selected trip
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded) return;
    const ml = window.maplibregl;

    if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
    if (!selectedTrip) return;

    const pos = selectedTrip.type === 'start' ? selectedTrip.start : selectedTrip.type === 'end' ? selectedTrip.end : selectedTrip.current;
    const label = selectedTrip.type === 'start' ? (selectedTrip.dispatchLocation || 'Start point') : selectedTrip.type === 'end' ? (selectedTrip.decantingLocation || 'End point') : 'Current position';
    const color = selectedTrip.type === 'start' ? '#2E7D32' : selectedTrip.type === 'end' ? '#D32F2F' : '#1565C0';
    const title = selectedTrip.type === 'start' ? 'Start' : selectedTrip.type === 'end' ? 'End' : 'Current';

    if (!pos) return;
    const popup = new ml.Popup({ closeButton: true, offset: [0, -12] })
      .setLngLat(pos)
      .setHTML(`<div style="padding:5px;font-weight:bold;max-width:180px"><div style="color:${color};margin-bottom:3px">${title}</div><div style="font-weight:normal">${label}</div></div>`)
      .addTo(map);
    popup.on('close', () => setSelectedTrip(null));
    popupRef.current = popup;
  }, [selectedTrip, mapLoaded]);

  // Geofences layer
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded) return;
    const features = geofences.map(geo => ({
      type: 'Feature',
      properties: { id: geo.id, selected: selectedGeofence?.id === geo.id },
      geometry: makeCirclePolygon([geo.center.lng, geo.center.lat], geo.radius),
    }));
    const src = map.getSource('geofences');
    if (src) src.setData({ type: 'FeatureCollection', features });
  }, [geofences, mapLoaded, selectedGeofence]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {viewAllTripsLoading && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.55)' }}>
          <SpinnerLoader />
        </div>
      )}
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default FleetOverviewMapLibre;
